import { useState, useEffect } from 'react';
import moment from 'moment';

/**
 * Helper function to safely convert to array
 */
const safeArray = (arr) => Array.isArray(arr) ? arr : [];

/**
 * Helper function to safely convert to object
 */
const safeObject = (obj) => obj && typeof obj === 'object' && !Array.isArray(obj) ? obj : {};

/**
 * Custom hook to manage service details and treatment plan data
 * Processes service details into a table-friendly format
 * @param {Object} patientPlanList - Raw patient plan data
 * @param {number} selectedPlanIndex - Index of selected plan
 * @returns {Object} Processed service data and date management
 */
export const useServiceDetails = (patientPlanList, selectedPlanIndex) => {
    const [patientPlanData, setPatientPlanData] = useState({
        dataSource: [],
        allDates: [],
        visibleDates: [],
    });
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
        // Handle nested API response structure
        const paginationData = Array.isArray(patientPlanList)
            ? patientPlanList
            : (patientPlanList?.data || patientPlanList || {});

        const patientPlans = paginationData?.docs || (paginationData?._id ? [paginationData] : []);
        const actualPatientPlan = patientPlans[selectedPlanIndex] || patientPlans[0] || {};
        if (actualPatientPlan?.serviceDetails) {
            const serviceDetails = actualPatientPlan.serviceDetails;
            const dates = Object.keys(serviceDetails).sort();

            // Use Map for efficient service grouping (O(n) complexity)
            const serviceMap = new Map();

            dates.forEach(date => {
                safeArray(serviceDetails[date]).forEach(service => {
                    const serviceKey = `${service.serviceName}_${service.startTime}`;
                    if (!serviceMap.has(serviceKey)) {
                        serviceMap.set(serviceKey, {
                            serviceName: service.serviceName,
                            startTime: service.startTime,
                            endTime: service.endTime,
                            treatmentName: service.treatmentName,
                        });
                    }

                    const serviceEntry = serviceMap.get(serviceKey);
                    serviceEntry[date] = {
                        totalMinutes: service.totalMinutes,
                        medicine: service.medicine,
                        medicineValue: service.medicineValue || null,
                    };
                });
            });

            const dataSource = Array.from(serviceMap.values()).map((item, index) => ({
                ...item,
                key: `${item.serviceName}_${item.startTime}_${index}`, // Add unique key for React
            }));
            setPatientPlanData({
                dataSource,
                allDates: dates,
                visibleDates: dates.slice(0, 2),
            });
        } else {
            setPatientPlanData({
                dataSource: [],
                allDates: [],
                visibleDates: [],
            });
        }
    }, [patientPlanList, selectedPlanIndex]);

    /**
     * Navigate through date pairs
     * @param {number} startIndex - Starting index for date slice
     */
    const processObjectsInPairs = (startIndex) => {
        const endIndex = Math.min(startIndex + 2, patientPlanData.allDates.length);
        const pair = patientPlanData.allDates.slice(startIndex, endIndex);

        if (pair.length === 0) {
            // Loop back to start
            setCurrentIndex(0);
            setPatientPlanData(prev => ({
                ...prev,
                visibleDates: prev.allDates.slice(0, 2),
            }));
        } else {
            setCurrentIndex(startIndex);
            setPatientPlanData(prev => ({
                ...prev,
                visibleDates: pair,
            }));
        }
    };

    /**
     * Update medicine value for a specific service and date
     * Auto-updates future dates with the same value
     * @param {number} recordIndex - Index of service record
     * @param {string} date - Date to update
     * @param {string} newValue - New medicine value
     */
    const handlePlanChange = (recordIndex, date, newValue) => {
        const newDataSource = [...patientPlanData.dataSource];

        if (newDataSource[recordIndex]?.[date]) {
            // Update current date
            newDataSource[recordIndex][date].medicineValue = newValue;

            // Auto-update future dates
            const currentDateIndex = patientPlanData.allDates.indexOf(date);
            const today = moment().startOf('day');
            const currentDate = moment(date, 'YYYY-MM-DD').startOf('day');

            // Only auto-update if current date is today or future, AND value is NOT "Not Taken"
            if (!currentDate.isBefore(today) && newValue !== 'Not Taken') {
                for (let i = currentDateIndex + 1; i < patientPlanData.allDates.length; i++) {
                    const futureDate = patientPlanData.allDates[i];
                    if (newDataSource[recordIndex][futureDate]) {
                        newDataSource[recordIndex][futureDate].medicineValue = newValue;
                    }
                }
            }

            setPatientPlanData(prev => ({
                ...prev,
                dataSource: newDataSource,
            }));
        }
    };

    return {
        patientPlanData,
        currentIndex,
        processObjectsInPairs,
        handlePlanChange,
    };
};
